import { SWRKey, type SWRSubscription, useSWRSubscription } from 'services/swr';
import { useUser } from 'services/User';

interface NotificationKey extends NonNullable<SWRKey> {
    userId: string;
}

interface Notification {
    id: string;
    title: string;
    message: string;
    timestamp: number;
}

interface NotificationMessage {
    action: 'get-notifications' | 'new-notification' | 'retain';
    userId?: string;
    notifications?: Notification[];
    notification?: Notification;
}

const notificationsSub: SWRSubscription<NotificationKey, NotificationMessage, Event> = (key, { next }) => {
    let socket: WebSocket;
    const ONE_HOUR = 3600000;
    const url = `${key.url}?id=${encodeURIComponent(key.userId)}`;

    try {
        socket = new WebSocket(url);
    } catch {
        return () => {};
    }

    let retainInterval: NodeJS.Timeout;

    socket.addEventListener('open', () => {
        socket.send(JSON.stringify({ action: 'get-notifications', userId: key.userId }));
        retainInterval = setInterval(() => {
            socket.send(JSON.stringify({ action: 'retain' }));
        }, ONE_HOUR);
    });

    socket.addEventListener('message', (event) => {
        const data: NotificationMessage = JSON.parse(event.data);

        switch (data.action) {
            case 'get-notifications':
                next(null, data);
                break;
            case 'new-notification':
                next(null, (prev) => {
                    const notifications = prev?.notifications ? [...prev.notifications] : [];

                    if (data.notification) {
                        const exists = notifications.some(({ id }) => id === data?.notification?.id);

                        if (!exists) {
                            notifications.unshift(data.notification);
                        }
                    }

                    return { ...data, notifications };
                });
                break;
            default:
                break;
        }
    });

    socket.addEventListener('error', (event) => next(event));

    socket.addEventListener('close', () => {
        if (retainInterval) {
            clearInterval(retainInterval);
        }
    });

    return () => {
        if (retainInterval) {
            clearInterval(retainInterval);
        }
        socket.close();
    };
};

export const useNotifications = () => {
    const { user } = useUser();
    const isAdmin = user?.org_role === 'admin';

    const notificationsKey =
        user?.sso_user_id && isAdmin && process.env.GATSBY_WEBSOCKET_URL
            ? { url: process.env.GATSBY_WEBSOCKET_URL, userId: user.sso_user_id }
            : null;

    return useSWRSubscription(notificationsKey, notificationsSub);
};
